import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Global scroll restoration.
 *
 * - PUSH / REPLACE navigations  → scroll to top (fresh page feel).
 * - POP navigations (back/forward) → restore the previously saved scroll
 *   position so users return to the exact place they left.
 *
 * Positions are keyed by `location.key` and stored in `sessionStorage`
 * so they survive full-page reloads within the same tab session but do
 * not leak across browser sessions.
 *
 * Works in Edge, Chrome, Firefox, Safari and mobile browsers — relies
 * only on `window.scrollTo` and `sessionStorage`.
 */
const STORAGE_KEY = "ofuesi:scroll-positions";
const MAX_ENTRIES = 50;

type PositionMap = Record<string, number>;

const readMap = (): PositionMap => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as PositionMap) : {};
  } catch {
    return {};
  }
};

const writeMap = (map: PositionMap) => {
  try {
    const entries = Object.entries(map);
    // Trim oldest entries first if we exceed the cap.
    const trimmed = entries.slice(-MAX_ENTRIES);
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(trimmed)));
  } catch {
    /* sessionStorage full or unavailable — silently ignore */
  }
};

export const useScrollRestoration = () => {
  const location = useLocation();
  const navType = useNavigationType();
  const lastKey = useRef<string | null>(null);

  // Tell the browser to stop fighting us with its own restoration.
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      const previous = window.history.scrollRestoration;
      window.history.scrollRestoration = "manual";
      return () => {
        window.history.scrollRestoration = previous;
      };
    }
  }, []);

  // Save the scroll position of the OUTGOING route before navigating away.
  useEffect(() => {
    const savePosition = () => {
      if (!lastKey.current) return;
      const map = readMap();
      map[lastKey.current] = window.scrollY;
      writeMap(map);
    };

    window.addEventListener("beforeunload", savePosition);
    return () => {
      savePosition();
      window.removeEventListener("beforeunload", savePosition);
    };
  }, [location.key]);

  // Restore (POP) or reset (PUSH/REPLACE) on every navigation.
  useEffect(() => {
    const key = location.key || "default";
    lastKey.current = key;

    if (navType === "POP") {
      const map = readMap();
      const target = map[key] ?? 0;
      // The new route may mount lazy chunks / fetch data, so the document
      // height grows over time. Retry restoring until we can actually reach
      // the saved offset (or we give up after ~1s).
      let attempts = 0;
      const tryRestore = () => {
        const max = Math.max(
          0,
          document.documentElement.scrollHeight - window.innerHeight,
        );
        const reachable = Math.min(target, max);
        window.scrollTo({ top: reachable, left: 0, behavior: "auto" });
        attempts += 1;
        if (Math.abs(window.scrollY - target) > 4 && attempts < 20) {
          window.setTimeout(tryRestore, 50);
        }
      };
      const id = window.requestAnimationFrame(tryRestore);
      return () => window.cancelAnimationFrame(id);
    }

    // Fresh navigation – start at top.
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.key, location.pathname, navType]);
};

export default useScrollRestoration;
